import React, { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
interface ScrollRevealProps {
  children: ReactNode;
  delay?: number;
  className?: string;
}

/**
 * Reveals content on scroll without ever being able to strand it hidden.
 *
 * Three independent paths set the visible state, so a missed intersection
 * callback cannot leave content permanently invisible:
 *  1. the intersection observer, for the normal scroll-into-view case
 *  2. a mount-time bounding-rect check, for anything already on screen
 *  3. a 1200ms failsafe timer, for everything else
 */
export function ScrollReveal({
  children,
  delay = 0,
  className = ''
}: ScrollRevealProps) {
  const prefersReducedMotion = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true
  });
  const [isVisible, setIsVisible] = useState(false);

  // Anything already within — or scrolled past — the viewport at mount shows
  // immediately, without waiting for an observer callback.
  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }
    const rect = element.getBoundingClientRect();
    if (rect.top < window.innerHeight) {
      setIsVisible(true);
    }
  }, []);

  // Failsafe: never stay hidden longer than 1200ms, whatever the observer does.
  useEffect(() => {
    const timer = window.setTimeout(() => setIsVisible(true), 1200);
    return () => window.clearTimeout(timer);
  }, []);
  useEffect(() => {
    if (isInView) {
      setIsVisible(true);
    }
  }, [isInView]);
  if (prefersReducedMotion) {
    return <div className={className} data-id="element-788" data-mp="jmffhz3bl9pt">{children}</div>;
  }
  return <motion.div ref={ref} className={className} initial={{
    opacity: 0,
    y: 16
  }} animate={isVisible ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 16
  }} transition={{
    duration: 0.4,
    delay,
    ease: [0.23, 1, 0.32, 1]
  }} data-id="element-789" data-mp="ol0fbxentrdq">
      {children}
    </motion.div>;
}